import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { groupOutlookService, GroupOutlook } from '@/services/chat/groupOutlookService';

interface GroupOutlookModalProps {
  visible: boolean;
  groupId: string;
  ticker: string | null;
  onClose: () => void;
  onViewMessages?: () => void;
}

export const GroupOutlookModal: React.FC<GroupOutlookModalProps> = ({
  visible,
  groupId,
  ticker,
  onClose,
  onViewMessages,
}) => {
  const [outlook, setOutlook] = useState<GroupOutlook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && ticker) {
      loadOutlook();
    }
  }, [visible, ticker, groupId]);

  const loadOutlook = async () => {
    if (!ticker) return;

    setLoading(true);
    setError(null);

    console.log(`📊 [GroupOutlook] Loading outlook for $${ticker} in group ${groupId}`);

    const result = await groupOutlookService.getTickerOutlook(groupId, ticker);

    if (result.error) {
      console.error(`❌ [GroupOutlook] Error loading outlook:`, result.error);
      setError(result.error);
    } else if (result.data) {
      console.log(`✅ [GroupOutlook] Loaded outlook for $${ticker}:`, result.data);
      setOutlook(result.data);
    }

    setLoading(false);
  };

  const renderConfidenceBar = (confidence: number) => {
    const percentage = Math.round(confidence * 100);
    const color =
      percentage >= 90
        ? Colors.market.profit
        : percentage >= 70
          ? Colors.accent.primary
          : percentage >= 50
            ? Colors.status.warning
            : Colors.status.error;

    return (
      <View style={styles.confidenceContainer}>
        <View style={styles.confidenceBarBg}>
          <View
            style={[styles.confidenceBarFill, { width: `${percentage}%`, backgroundColor: color }]}
          />
        </View>
        <Text style={[styles.confidenceText, { color }]}>{percentage}%</Text>
      </View>
    );
  };

  const renderSentiment = (sentiment: GroupOutlook['sentiment']) => {
    const total = sentiment.bullish + sentiment.bearish + sentiment.neutral;
    if (total === 0) return null;

    const bullishPct = Math.round((sentiment.bullish / total) * 100);
    const bearishPct = Math.round((sentiment.bearish / total) * 100);
    const neutralPct = Math.round((sentiment.neutral / total) * 100);

    return (
      <View style={styles.sentimentContainer}>
        <View style={styles.sentimentBar}>
          {bullishPct > 0 && (
            <View
              style={[
                styles.sentimentSegment,
                { width: `${bullishPct}%`, backgroundColor: Colors.market.profit },
              ]}
            />
          )}
          {bearishPct > 0 && (
            <View
              style={[
                styles.sentimentSegment,
                { width: `${bearishPct}%`, backgroundColor: Colors.market.loss },
              ]}
            />
          )}
          {neutralPct > 0 && (
            <View
              style={[
                styles.sentimentSegment,
                { width: `${neutralPct}%`, backgroundColor: Colors.text.tertiary },
              ]}
            />
          )}
        </View>
        <View style={styles.sentimentLegend}>
          {bullishPct > 0 && (
            <View style={styles.sentimentItem}>
              <View style={[styles.sentimentDot, { backgroundColor: Colors.market.profit }]} />
              <Text style={styles.sentimentLabel}>Bullish {bullishPct}%</Text>
            </View>
          )}
          {bearishPct > 0 && (
            <View style={styles.sentimentItem}>
              <View style={[styles.sentimentDot, { backgroundColor: Colors.market.loss }]} />
              <Text style={styles.sentimentLabel}>Bearish {bearishPct}%</Text>
            </View>
          )}
          {neutralPct > 0 && (
            <View style={styles.sentimentItem}>
              <View style={[styles.sentimentDot, { backgroundColor: Colors.text.tertiary }]} />
              <Text style={styles.sentimentLabel}>Neutral {neutralPct}%</Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Ionicons name="analytics" size={24} color={Colors.ai.primary} />
            <Text style={styles.headerTitle}>Group Outlook</Text>
          </View>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={Colors.text.primary} />
          </TouchableOpacity>
        </View>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Colors.accent.primary} />
            <Text style={styles.loadingText}>Analyzing conversation...</Text>
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <Ionicons name="alert-circle-outline" size={64} color={Colors.status.error} />
            <Text style={styles.errorTitle}>Error Loading Outlook</Text>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={loadOutlook}>
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : outlook ? (
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* Ticker Header */}
            <View style={styles.tickerHeader}>
              <Text style={styles.tickerSymbol}>${ticker}</Text>
              <Text style={styles.tickerSubtitle}>
                {outlook.active_days_count > 0
                  ? `${outlook.active_days_count} ${outlook.active_days_count === 1 ? 'day' : 'days'} of discussion`
                  : 'No discussion yet'}
              </Text>
              {outlook.first_mentioned_at && (
                <Text style={styles.tickerFirstMention}>
                  First mentioned{' '}
                  {new Date(outlook.first_mentioned_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Text>
              )}
            </View>

            {/* Historical Stats */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>📊 Activity</Text>
              <View style={styles.statsGrid}>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{outlook.total_messages}</Text>
                  <Text style={styles.statLabel}>Total Messages</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{outlook.active_days_count}</Text>
                  <Text style={styles.statLabel}>Active Days</Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={styles.statValue}>{outlook.total_participants}</Text>
                  <Text style={styles.statLabel}>Participants</Text>
                </View>
              </View>
            </View>

            {/* Overall Sentiment */}
            {outlook.overall_sentiment && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>📈 Overall Sentiment</Text>
                <View style={styles.overallSentimentContainer}>
                  <Text
                    style={[
                      styles.overallSentimentText,
                      {
                        color:
                          outlook.overall_sentiment === 'bullish'
                            ? Colors.market.profit
                            : outlook.overall_sentiment === 'bearish'
                              ? Colors.market.loss
                              : Colors.text.secondary,
                      },
                    ]}
                  >
                    {outlook.overall_sentiment.toUpperCase()}
                  </Text>
                  {outlook.confidence_score > 0 && (
                    <Text style={styles.overallSentimentConfidence}>
                      {Math.round(outlook.confidence_score * 100)}% confidence
                    </Text>
                  )}
                </View>
              </View>
            )}

            {/* Executive Summary */}
            {outlook.executive_summary && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>💡 Executive Summary</Text>
                <Text style={styles.executiveSummaryText}>{outlook.executive_summary}</Text>
              </View>
            )}

            {/* Context Confidence */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>🎯 Context Confidence</Text>
              {renderConfidenceBar(outlook.context.confidence)}
              <Text style={styles.contextSource}>
                Source: <Text style={styles.contextSourceValue}>{outlook.context.source}</Text>
              </Text>
            </View>

            {/* Related Tickers */}
            {outlook.context.related_tickers.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>🔗 Related Tickers</Text>
                <View style={styles.tickersList}>
                  {outlook.context.related_tickers.map((related, index) => (
                    <View key={index} style={styles.relatedTickerItem}>
                      <Text style={styles.relatedTickerSymbol}>${related.ticker}</Text>
                      <Text style={styles.relatedTickerCount}>
                        {related.count} mention{related.count !== 1 ? 's' : ''}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Active Participants */}
            {outlook.participants.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>👥 Active Participants</Text>
                {outlook.participants.map((participant, index) => (
                  <View key={index} style={styles.participantItem}>
                    <View style={styles.participantAvatar}>
                      <Text style={styles.participantInitial}>
                        {participant.user_name.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                    <View style={styles.participantInfo}>
                      <Text style={styles.participantName}>{participant.user_name}</Text>
                      <Text style={styles.participantMessages}>
                        {participant.message_count} message
                        {participant.message_count !== 1 ? 's' : ''}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* Sentiment Analysis */}
            {(outlook.sentiment.bullish > 0 ||
              outlook.sentiment.bearish > 0 ||
              outlook.sentiment.neutral > 0) && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>📈 Sentiment Analysis</Text>
                {renderSentiment(outlook.sentiment)}
                <Text style={styles.sentimentNote}>Based on keyword analysis (Beta)</Text>
              </View>
            )}

            {/* Key Phrases */}
            {outlook.key_phrases && outlook.key_phrases.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>💬 Key Phrases</Text>
                {outlook.key_phrases.map((phrase, index) => (
                  <View key={index} style={styles.phraseItem}>
                    <Ionicons name="chatbox-ellipses-outline" size={16} color={Colors.ai.primary} />
                    <Text style={styles.phraseText}>"{phrase}"</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Summary */}
            {outlook.summary && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>📝 Summary</Text>
                <Text style={styles.summaryText}>{outlook.summary}</Text>
              </View>
            )}

            {/* View Messages Button */}
            <TouchableOpacity
              style={styles.viewMessagesButton}
              onPress={() => {
                onClose();
                if (onViewMessages) {
                  onViewMessages();
                }
              }}
            >
              <Ionicons name="chatbubbles" size={20} color={Colors.text.primary} />
              <Text style={styles.viewMessagesButtonText}>View All Messages about ${ticker}</Text>
            </TouchableOpacity>

            <View style={styles.bottomSpacing} />
          </ScrollView>
        ) : (
          <View style={styles.emptyContainer}>
            <Ionicons name="information-circle-outline" size={64} color={Colors.text.tertiary} />
            <Text style={styles.emptyText}>No data available</Text>
          </View>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  closeButton: {
    padding: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    color: Colors.text.tertiary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 16,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  content: {
    flex: 1,
  },
  tickerHeader: {
    alignItems: 'center',
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  tickerSymbol: {
    fontSize: 32,
    fontWeight: '700',
    color: Colors.accent.primary,
    marginBottom: 4,
  },
  tickerSubtitle: {
    fontSize: 13,
    color: Colors.text.tertiary,
    marginBottom: 4,
  },
  tickerFirstMention: {
    fontSize: 11,
    color: Colors.text.tertiary,
    fontStyle: 'italic',
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 12,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    color: Colors.accent.primary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
  confidenceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  confidenceBarBg: {
    flex: 1,
    height: 8,
    backgroundColor: Colors.background.tertiary,
    borderRadius: 4,
    overflow: 'hidden',
  },
  confidenceBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  confidenceText: {
    fontSize: 14,
    fontWeight: '600',
    minWidth: 40,
    textAlign: 'right',
  },
  contextSource: {
    fontSize: 12,
    color: Colors.text.tertiary,
    marginTop: 8,
  },
  contextSourceValue: {
    fontWeight: '600',
    color: Colors.text.secondary,
    textTransform: 'capitalize',
  },
  tickersList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  relatedTickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.secondary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    gap: 6,
  },
  relatedTickerSymbol: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  relatedTickerCount: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
  participantItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  participantAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  participantInitial: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  participantInfo: {
    flex: 1,
  },
  participantName: {
    fontSize: 15,
    fontWeight: '500',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  participantMessages: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
  sentimentContainer: {
    gap: 12,
  },
  sentimentBar: {
    flexDirection: 'row',
    height: 12,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: Colors.background.tertiary,
  },
  sentimentSegment: {
    height: '100%',
  },
  sentimentLegend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  sentimentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sentimentDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  sentimentLabel: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  sentimentNote: {
    fontSize: 11,
    color: Colors.text.tertiary,
    marginTop: 8,
    fontStyle: 'italic',
  },
  phraseItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8,
  },
  phraseText: {
    flex: 1,
    fontSize: 14,
    color: Colors.text.secondary,
    lineHeight: 20,
  },
  summaryText: {
    fontSize: 14,
    color: Colors.text.secondary,
    lineHeight: 22,
  },
  viewMessagesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.accent.primary,
    marginHorizontal: 16,
    marginTop: 16,
    paddingVertical: 14,
    borderRadius: 8,
    gap: 8,
  },
  viewMessagesButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  bottomSpacing: {
    height: 32,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyText: {
    fontSize: 16,
    color: Colors.text.tertiary,
    marginTop: 16,
  },
  overallSentimentContainer: {
    alignItems: 'center',
    paddingVertical: 12,
  },
  overallSentimentText: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 6,
  },
  overallSentimentConfidence: {
    fontSize: 13,
    color: Colors.text.tertiary,
  },
  executiveSummaryText: {
    fontSize: 14,
    color: Colors.text.secondary,
    lineHeight: 22,
    backgroundColor: Colors.background.secondary,
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: Colors.ai.primary,
  },
});
